import type { Experiment } from '@carvana/experiment/SharedTypes';
import { Clickable } from '@carvana/merch-components';
import { useState, useEffect, useCallback } from 'react';
import { experimentNames, experiments } from '~/constants/common/experiments';
import { useIsNative } from '~/context/common/DeviceContext';
import {
  Button as StyledButton,
  Heading,
  Container
} from './PassengerTools.styles';
import { copyToClipboard } from './utils/createTestUser';

type OwnProps = {
  onClose: () => void;
};

const FORCE_FRESH_EXPERIENCE = 'forceFreshExperience';
const experimentsByName: Record<string, Experiment> = experiments;

export default function Experiments({ onClose }: OwnProps) {
  const [experimentStates, setExperimentStates] = useState<
    Record<string, string>
  >({});
  const isNative = useIsNative();

  const isExperimentRelevantForPath = useCallback(
    (experimentName: string, currentPath: string): boolean => {
      if (experimentName.includes('vdp')) {
        return currentPath.startsWith('/vehicle');
      } else if (experimentName.includes('srp')) {
        return currentPath.startsWith('/cars');
      } else if (experimentName.includes('home')) {
        return currentPath === '/';
      } else {
        const hasSpecificPageType = ['vdp', 'srp', 'home'].some(pageType =>
          experimentName.includes(pageType)
        );

        return !hasSpecificPageType;
      }
    },
    []
  );

  const getRelevantExperiments = useCallback(() => {
    const currentPath = window.location.pathname;

    const filteredExperiments = experimentNames.filter(experimentName =>
      isExperimentRelevantForPath(experimentName, currentPath)
    );

    if (currentPath === '/') {
      return [FORCE_FRESH_EXPERIENCE, ...filteredExperiments];
    }

    return filteredExperiments;
  }, [isExperimentRelevantForPath]);

  const relevantExperiments = getRelevantExperiments();

  useEffect(() => {
    const currentStates: Record<string, string> = {};
    const urlParams = new URLSearchParams(window.location.search);

    const forceFreshValue = urlParams.get(FORCE_FRESH_EXPERIENCE);
    currentStates[FORCE_FRESH_EXPERIENCE] = forceFreshValue || '';

    experimentNames.forEach(experimentName => {
      const urlValue = urlParams.get(experimentName);
      currentStates[experimentName] = urlValue || '';
    });

    setExperimentStates(currentStates);
  }, []);

  const handleExperimentToggle = useCallback(
    (experimentName: string, value: string) => {
      setExperimentStates(prev => {
        if (experimentName === FORCE_FRESH_EXPERIENCE) {
          return {
            ...prev,
            [experimentName]: value
          };
        }

        const currentValue = prev[experimentName] || '';
        const newValue = currentValue === value ? '' : value;

        return {
          ...prev,
          [experimentName]: newValue
        };
      });
    },
    []
  );

  const handleApply = useCallback(() => {
    const currentUrl = new URL(window.location.href);
    let urlChanged = false;

    relevantExperiments.forEach(experimentName => {
      const value = experimentStates[experimentName] || '';
      const currentUrlParam = currentUrl.searchParams.get(experimentName);

      if (value === '') {
        if (currentUrlParam !== null) {
          currentUrl.searchParams.delete(experimentName);
          urlChanged = true;
        }
      } else {
        if (currentUrlParam !== value) {
          currentUrl.searchParams.set(experimentName, value);
          urlChanged = true;
        }
      }
    });

    if (urlChanged) {
      if (!isNative) {
        window.location.href = currentUrl.toString();
      }
    } else {
      onClose();
    }
  }, [relevantExperiments, experimentStates, isNative, onClose]);

  const handleClearAll = useCallback(() => {
    setExperimentStates(prev => {
      const newStates = { ...prev };
      newStates[FORCE_FRESH_EXPERIENCE] = '';
      experimentNames.forEach(name => {
        newStates[name] = '';
      });

      return newStates;
    });
  }, []);

  const handleRefresh = useCallback(() => {
    const currentStates: Record<string, string> = {};
    const urlParams = new URLSearchParams(window.location.search);

    const forceFreshValue = urlParams.get(FORCE_FRESH_EXPERIENCE);
    currentStates[FORCE_FRESH_EXPERIENCE] = forceFreshValue || '';

    experimentNames.forEach(experimentName => {
      const urlValue = urlParams.get(experimentName);
      currentStates[experimentName] = urlValue || '';
    });

    setExperimentStates(currentStates);
  }, []);

  const getExperimentVariants = useCallback(
    (experimentName: string): string[] => {
      if (experimentName === FORCE_FRESH_EXPERIENCE) {
        return ['true'];
      }

      return (
        experimentsByName[experimentName]?.buckets ?? ['control', 'treatment']
      );
    },
    []
  );

  return (
    <Container>
      <Heading>Set Experiments</Heading>
      <div className="max-h-[400px] flex-1 overflow-y-auto">
        {relevantExperiments.length === 0 ? (
          <div className="py-16 text-center text-white">
            No experiments configured for this page
            <div className="mt-4 text-sm text-gray-400">
              Current path: {window.location.pathname}
            </div>
          </div>
        ) : (
          <div className="space-y-12">
            {relevantExperiments.map(experimentName => {
              const variants = getExperimentVariants(experimentName);
              const currentValue = experimentStates[experimentName] || '';
              const isForceFresh = experimentName === FORCE_FRESH_EXPERIENCE;

              return (
                <div
                  key={experimentName}
                  className="border-b border-grey-3 pb-12"
                >
                  <div className="mb-8 flex items-center gap-8">
                    <div className="font-medium text-white">
                      {experimentName}
                    </div>
                    <Clickable
                      element="button"
                      onClick={() => copyToClipboard(experimentName)}
                      className="rounded-xs bg-gray-600 px-8 py-4 text-xs text-white hover:bg-gray-500"
                      data-analytics-id={null}
                      data-testid="copy-experiment-name"
                      title="Copy experiment name"
                    >
                      Copy
                    </Clickable>
                  </div>
                  {isForceFresh ? (
                    <label className="flex cursor-pointer items-center gap-4 text-white">
                      <input
                        type="checkbox"
                        checked={currentValue === 'true'}
                        onChange={e =>
                          handleExperimentToggle(
                            experimentName,
                            e.target.checked ? 'true' : ''
                          )
                        }
                        className="h-16 w-16 checked:accent-background-primary"
                      />
                      <span>Enable</span>
                    </label>
                  ) : (
                    <div className="flex gap-8">
                      {variants.map(variant => (
                        <label
                          key={variant}
                          className="flex cursor-pointer items-center gap-4 text-white"
                        >
                          <input
                            type="radio"
                            name={experimentName}
                            value={variant}
                            checked={currentValue === variant}
                            onChange={e =>
                              handleExperimentToggle(
                                experimentName,
                                e.target.value
                              )
                            }
                            className="text-blue-500 checked:accent-background-primary"
                          />
                          <span className="capitalize">{variant}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="flex items-center justify-between">
        <div className="flex gap-md">
          <StyledButton
            type="button"
            onClick={handleRefresh}
            className="bg-blue-500 text-sm text-white hover:bg-blue-600"
            data-analytics-id={null}
            data-testid="refresh-experiments"
          >
            Refresh
          </StyledButton>
          <StyledButton
            type="button"
            onClick={handleClearAll}
            className="bg-red-500 text-sm text-white hover:bg-red-600"
            data-analytics-id={null}
            data-testid="clear-all-experiments"
          >
            Clear All
          </StyledButton>
        </div>
        <StyledButton
          type="button"
          onClick={handleApply}
          data-analytics-id={null}
          data-testid="apply-experiments"
        >
          Apply
        </StyledButton>
      </div>
    </Container>
  );
}
